import getenv from 'getenv';
import * as Sentry from '@sentry/nextjs';

import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import * as api from '@tmh-website/services/electricity-tariff/electricity-tariff.client.service';

import { BusinessUnitChargingWeb2CaseFormValues } from './BusinessUnitChargingWeb2CaseForm.schema';
import {
  resolveWeb2CasePayloadCaptchaSettings,
  resolveWeb2CasePayloadOrgId,
  resolveWeb2CasePayloadRetUrl,
  generateDescription,
} from '../StaticForms.utils';

type FieldName = keyof BusinessUnitChargingWeb2CaseFormValues;
type FieldNameWithoutCaptcha = Exclude<FieldName, 'g-recaptcha-response'>;
type FieldLabels = Record<FieldNameWithoutCaptcha, string>;

const removeUndefinedProperties = (obj: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as Record<string, string>;

const postBusinessUnitSolutionsWeb2CaseForm = async (
  values: BusinessUnitChargingWeb2CaseFormValues,
  fieldLabels: FieldLabels
): Promise<void> => {
  const SALESFORCE_W2C_ENDPOINT: string = getenv(
    'NEXT_PUBLIC_SALESFORCE_W2C_ENDPOINT',
    process.env.NEXT_PUBLIC_SALESFORCE_W2C_ENDPOINT
  );

  // Remove undefined properties from the values object, some of them are optional, but already existing as initial values (with undefined value).
  const removedUndefinedProperties = removeUndefinedProperties(values);

  const payloadValues = {
    ...removedUndefinedProperties,
    CustomerType__c: 'Private',
    name: `${values.first_name} ${values.last_name}`,
    subject: `W2C Business Unit Charging (${values.question})`,
    orgid: resolveWeb2CasePayloadOrgId(),
    retURL: resolveWeb2CasePayloadRetUrl(),
    priority: 'Low',
    type: 'ChargePilot',
    reason: 'General Support',
    captcha_settings: resolveWeb2CasePayloadCaptchaSettings(),
    description: generateDescription(values, fieldLabels),
  };

  await fetch(SALESFORCE_W2C_ENDPOINT, {
    body: new URLSearchParams({ ...payloadValues }),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    method: 'POST',
    mode: 'no-cors',
  });
};

type UseChargingSolutionsWeb2CaseFormResult = {
  mutateAsync: UseMutateAsyncFunction<void, unknown, BusinessUnitChargingWeb2CaseFormValues, unknown>;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  fieldLabels: FieldLabels;
};

const sendAdminEmail = (variables: BusinessUnitChargingWeb2CaseFormValues, adminEmail?: string) => {
  api.postSubmitContactForm({
    adminContactDetails: {
      email: adminEmail || '',
    },
    contactDetails: {
      email: variables.email,
      phone: variables.phone,
    },
    checkRecaptcha: false,
    subject: 'W2C Business Unit Charging',
    gRecaptchaResponse: variables['g-recaptcha-response'],
    issueType: variables.question || '',
    message: variables.message || '',
    personalDetails: {
      firstName: variables.first_name,
      lastName: variables.last_name,
    },
  });
};

/**
 * Hook to handle the submission of the Business Unit Charging W2C form.
 * Provides mutation functionality and field labels for the form.
 *
 * @param {string} adminEmail - The optional email address of the admin who will receive a notification email
 *                              after a successful form submission. This email is used to inform
 *                              the admin about the submitted support request details.
 **/

export const useBusinessChargingSolutionsWeb2CaseForm = (
  adminEmail?: string
): UseChargingSolutionsWeb2CaseFormResult => {
  const { t } = useTranslation();

  const fieldLabels: Record<FieldNameWithoutCaptcha, string> = {
    first_name: t('global:label.firstName'),
    last_name: t('global:label.lastName'),
    postal_code: t('blocks:staticForms.zip'),
    street: t('blocks:staticForms.streetAndHauseNumber'),
    city: t('blocks:staticForms.city'),
    phone: t('global:label.phone'),
    email: t('global:label.email'),
    question: t('blocks:staticForms.issueTypeLabel'),
    invoice_order_number: t('blocks:staticForms.payment.invoiceLabel'),
    order_number: t('blocks:staticForms.orderNumber'),
    shipping_delivery: t('blocks:staticForms.issueTypeLabel'),
    message: t('blocks:staticForms.messageLabel'),
    manufacturer: t('global:label.manufacturer'),
    company: t('global:label.company'),
    location_of_charging_infrastrucutre: 'Standort der Ladeinfrastruktur',
  };

  const { mutateAsync, isError, isLoading, isSuccess } = useMutation({
    mutationFn: (values: BusinessUnitChargingWeb2CaseFormValues) => {
      return postBusinessUnitSolutionsWeb2CaseForm(values, fieldLabels);
    },
    onError: error => {
      Sentry.captureException(error);
    },
    onSuccess: (_, variables) => {
      sendAdminEmail(variables, adminEmail);
    },
  });

  return {
    mutateAsync,
    isError,
    isLoading,
    isSuccess,
    fieldLabels,
  };
};
